import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiEvents } from '../../lib/apiEvents.js';
import { useI18n } from '../../i18n/index.js';

/**
 * „המערכת מתעדכנת — מתחברת מחדש לבד" (אלי 19/10/2026).
 *
 * עדכון מפעיל את השרת מחדש לכדקה, ובאותה דקה עמדה ברצפה הציגה „לא ניתן להגיע
 * לשרת. יש לבדוק את חיבור הרשת" — ונשארה כך עד שמישהו לחץ שוב. כאן: בקשה שלא
 * הגיעה (או 502/503/504) מדליקה פס עליון, בודקים את השרת כל 3 שניות, וכשהוא
 * עונה — מרעננים את כל נתוני המסך, מודיעים `reconnected` (מסך הכניסה מנסה שוב
 * לבד) ומכבים את הפס. מעל ה-Router: משרד, קיוסקים ומסכי קיר.
 */
export function ServerReconnect(): JSX.Element | null {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [down, setDown] = useState(false);

  useEffect(() => apiEvents.on('unreachable', () => setDown(true)), []);

  useEffect(() => {
    if (!down) return;
    let stop = false;
    const tick = async (): Promise<void> => {
      try {
        const r = await fetch('/api/health', { cache: 'no-store', credentials: 'include' });
        // השרת עונה = חזר. ⚠ גם 503: health מחזיר 503 כשמשהו בו לא תקין (מיגרציה,
        // ניתוב התראות) — וזה לא „אין חיבור"; רק 502/504 הם nginx בלי שרת מאחוריו.
        if (r.status !== 502 && r.status !== 504 && !stop) {
          setDown(false);
          void qc.invalidateQueries();
          apiEvents.emit('reconnected');
          return;
        }
      } catch { /* still down */ }
      if (!stop) setTimeout(() => { void tick(); }, 3000);
    };
    const first = setTimeout(() => { void tick(); }, 3000);
    return () => { stop = true; clearTimeout(first); };
  }, [down, qc]);

  if (!down) return null;
  return (
    <div role="status" aria-live="polite" data-testid="server-reconnect"
      className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-2 bg-amber-500 px-4 pb-1.5 pt-[calc(0.375rem+env(safe-area-inset-top,0px))] text-sm font-bold text-white shadow">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {t('net.reconnecting')}
    </div>
  );
}
